/** @jsxImportSource react */
import { useState } from 'react'
import { Box, Button, Checkbox, Chip, FormControlLabel, Stack, Typography } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { processAPI, recordsAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { getErrorList, getErrorMessage } from '../../utils/apiErrors'
import { useNotify } from '../common/NotificationProvider'
import ApprovalAskDialog, { approvalNeeded } from './ApprovalAskDialog'
import ApprovalCard from './ApprovalCard'

/** Where the record is in its process: stages, the next steps, the checklist, a waiting approval. */
const RecordProcess = ({ record, canEdit }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const [ask, setAsk] = useState(null)
  const { data: p } = useQuery({ queryKey: [tenantId, 'process', 'record', record.id], queryFn: () => processAPI.path(record.id) })
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: [tenantId, 'process', 'record', record.id] })
    queryClient.invalidateQueries({ queryKey: [tenantId, 'records'] })
  }
  const tick = useMutation({
    mutationFn: ({ item, done }) => processAPI.tick(record.id, item, done),
    onSuccess: (path) => queryClient.setQueryData([tenantId, 'process', 'record', record.id], path),
  })
  const move = useMutation({
    mutationFn: (to) => recordsAPI.patch(record.id, { [p.stageField]: to }),
    onSuccess: () => { refresh(); notify('Moved') },
    onError: (e, to) => {
      if (approvalNeeded(e)) setAsk({ recordId: record.id, changes: { [p.stageField]: to }, details: e.response.data.details })
      else notify([getErrorMessage(e), ...getErrorList(e)].join(' · '), 'error')
    },
  })
  if (!p) return null
  const label = (v) => p.stages.find((s) => s.value === v)?.label || v
  const at = p.stages.findIndex((s) => s.value === p.stage)
  const checklistOpen = p.checklist.some((c) => !c.done)
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Process</Typography>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
        {p.stages.map((s, i) => (
          <Chip key={s.value} size="small" label={s.label} variant={s.value === p.stage ? 'filled' : 'outlined'}
            color={s.value === p.stage ? 'primary' : 'default'} sx={{ opacity: at >= 0 && i < at ? 0.6 : 1 }} />
        ))}
      </Stack>
      {p.pending ? <ApprovalCard a={p.pending} /> : (
        <>
          {p.checklist.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Before leaving {label(p.stage)}:</Typography>
              {p.checklist.map((c) => (
                <FormControlLabel key={c.item} sx={{ display: 'flex', ml: 0 }}
                  control={<Checkbox size="small" checked={c.done} disabled={!canEdit || tick.isPending} onChange={(e) => tick.mutate({ item: c.item, done: e.target.checked })} />}
                  label={<Typography variant="body2">{c.item}{c.done && <Typography component="span" variant="caption" color="text.secondary"> · {c.doneBy}</Typography>}</Typography>} />
              ))}
            </Box>
          )}
          {canEdit && p.next.length > 0 && (
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
              {p.next.map((to) => (
                <Button key={to} size="small" variant="outlined" endIcon={<ArrowForwardIcon />} disabled={move.isPending || checklistOpen}
                  onClick={() => move.mutate(to)}>{label(to)}</Button>
              ))}
            </Stack>
          )}
        </>
      )}
      {ask && <ApprovalAskDialog ask={ask} onClose={() => setAsk(null)} onAsked={refresh} />}
    </Box>
  )
}

export default RecordProcess
